import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageProvider';
import { translations } from '../i18n/translations';

const CaseStudiesPage: React.FC = () => {
  const { language } = useLanguage();
  const dict = translations[language].caseStudiesPage;
  const caseStudies = dict.cases;

  return (
    <div className="py-16 md:py-24">
      {/* Hero Section */}
      <section className="bg-sand py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-navy mb-6 font-space">{dict.heroTitle}</h1>
            <p className="text-xl md:text-2xl text-slate mb-8 leading-relaxed">{dict.heroSub}</p>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} id={`case-study-${index + 1}`} className="bg-sand rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="aspect-video bg-navy/10 flex items-center justify-center">
                  {study.client.toLowerCase().includes('univers') ? (
                    <img src="/TUe-logo-square-descriptor-stack-scarlet-L.png" alt="Universiteit logo" className="w-full h-full object-contain" />
                  ) : (study.title.toLowerCase().includes('crm') || (Array.isArray(study.tags) && study.tags.join(' ').toLowerCase().includes('crm'))) ? (
                    <img src="/renewable.png" alt="CRM logo" className="w-full h-full object-contain" />
                  ) : (study.client.toLowerCase().includes('maps untold') || study.title.toLowerCase().includes('city guide')) ? (
                    <img src="/cropped-maps-untold-logo-3-1-4-scaled-1.png" alt="Maps Untold logo" className="w-full h-full object-contain" />
                  ) : (study.client.toLowerCase().includes('dr. pitt') || study.title.toLowerCase().includes('hr assistant')) ? (
                    <img src="/drpitt.png" alt="Dr. Pitt logo" className="w-full h-full object-contain" />
                  ) : (study.client.toLowerCase().includes('agenttailor') || study.client.toLowerCase().includes('intern')) ? (
                    <div className="text-2xl md:text-3xl font-bold text-navy font-space hover:text-navy/80 transition-colors">
                      AgentTailor.ai
                    </div>
                  ) : (
                    <svg className="w-16 h-16 text-navy/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  )}
                </div>
                
                <div className="p-8">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {study.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="px-3 py-1 bg-sky/10 text-navy text-sm rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-navy mb-2 font-space">
                    {study.title}
                  </h3>
                  <p className="text-slate font-medium mb-4">{study.client}</p>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <h4 className="font-semibold text-navy mb-1">{language === 'en' ? 'Context:' : 'Context:'}</h4>
                      <p className="text-slate text-sm">{study.context}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-navy mb-1">{language === 'en' ? 'Approach:' : 'Aanpak:'}</h4>
                      {study.approach.includes('\n•') ? (
                        <div className="text-slate text-sm">
                          {study.approach.split('\n').map((line, index) => {
                            if (line.startsWith('•')) {
                              return (
                                <div key={index} className="flex items-start mb-1">
                                  <span className="text-sky mr-2 mt-0.5">•</span>
                                  <span>{line.substring(1).trim()}</span>
                                </div>
                              );
                            } else {
                              return line.trim() ? <p key={index} className="mb-2">{line}</p> : null;
                            }
                          })}
                        </div>
                      ) : (
                        <p className="text-slate text-sm">{study.approach}</p>
                      )}
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-navy mb-1">{language === 'en' ? 'Result:' : 'Resultaat:'}</h4>
                      <p className="text-slate text-sm">{study.result}</p>
                    </div>
                  </div>
                  
                  <div className="border-t border-navy/10 pt-4">
                    <h4 className="font-semibold text-navy mb-3">{language === 'en' ? 'Key Metrics:' : 'Kerncijfers:'}</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {study.metrics.map((metric, metricIndex) => (
                        <div key={metricIndex} className="flex items-center">
                          <svg className="w-4 h-4 text-sky mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-slate text-sm font-medium">{metric}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Automation Blueprint CTA */}
      <section className="py-16 md:py-24 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-sand rounded-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6 font-space">
              {dict.blueprintTitle}
            </h2>
            <p className="text-xl text-slate mb-8 max-w-3xl mx-auto">
              {dict.blueprintSub}
            </p>
            <Link 
              to="/contact"
              className="bg-sky text-navy px-8 py-4 rounded-lg font-semibold hover:bg-sky/90 transition-colors duration-200 inline-block"
            >
              {dict.blueprintCta}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
